// Vision Conversational Deployment · C2 · current-frame snapshot.
//
// Reads the CURRENT frame from the live Vision preview <video> element
// (published on window.__leviVisionPreviewEl by useVideoWalkSession)
// and returns a JPEG Blob suitable for FormData upload. Non-destructive:
// the underlying MediaStream and MediaRecorder are untouched.
//
// Returns null when:
//   - the preview element is not mounted
//   - the video has no dimensions yet (metadata not loaded)
//   - the browser's canvas.toBlob is unavailable
//   - any step throws
//
// Do NOT create a second Vision inference architecture. This helper
// only extracts the frame; the backend adapter reuses the existing
// multimodal Vision inference primitive (run_recommendation).

const MAX_EDGE_PX = 1024; // downscale so payloads stay small

async function _blobFromCanvas(canvas, mime, quality) {
  return new Promise((resolve) => {
    try {
      if (typeof canvas.toBlob === "function") {
        canvas.toBlob((blob) => resolve(blob || null), mime, quality);
      } else {
        // Fallback: dataURL → Blob (older Safari edge cases).
        const dataUrl = canvas.toDataURL(mime, quality);
        const idx = dataUrl.indexOf(",");
        if (idx < 0) return resolve(null);
        const bin = atob(dataUrl.slice(idx + 1));
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        resolve(new Blob([arr], { type: mime }));
      }
    } catch { resolve(null); }
  });
}

export async function captureVisionSnapshot(opts = {}) {
  try {
    if (typeof window === "undefined") return null;
    let videoEl = window.__leviVisionPreviewEl || null;
    // Fallback: look up by the recorder's data-testid so the C2 path
    // works even before bindPreview has published the ref.
    if (!videoEl && typeof document !== "undefined") {
      videoEl = document.querySelector('[data-testid="video-walk-preview"]');
    }
    if (!videoEl) return null;
    const vw = videoEl.videoWidth || 0;
    const vh = videoEl.videoHeight || 0;
    if (!vw || !vh) return null;
    // Downscale so payloads stay small; preserves aspect ratio.
    const maxEdge = Math.min(
      MAX_EDGE_PX,
      Math.max(vw, vh),
    );
    const ratio = Math.min(1, maxEdge / Math.max(vw, vh));
    const cw = Math.max(1, Math.round(vw * ratio));
    const ch = Math.max(1, Math.round(vh * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    try {
      ctx.drawImage(videoEl, 0, 0, cw, ch);
    } catch { return null; }
    const mime = (opts && opts.mime) || "image/jpeg";
    const quality = (opts && typeof opts.quality === "number")
      ? opts.quality : 0.82;
    const blob = await _blobFromCanvas(canvas, mime, quality);
    return blob || null;
  } catch { return null; }
}
